import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import clsx from "clsx";
import Input from "components/Input";
import Button from "components/Button";
import Modal from "components/Modal";
import Textarea from "components/Textarea";
import Select from "components/Select";
import utilsService from "api/utils/utils.service";
import { ImageSuccessLogo } from "images";
import { useValidationSchema } from "i18n/yup";
import { httpStatusOf } from "utils/httpStatusOf";

interface ContactFormData {
  firstName: string;
  lastName: string;
  businessEmail: string;
  companyName: string;
  companySize: string;
  message: string;
}

interface ContactUsFormProps {
  show: boolean;
  onClose: () => void;
  type: string;
  backdropDisabled?: boolean;
}

const COMPANY_SIZE_OPTIONS = [
  { id: 1, title: "1-9" },
  { id: 2, title: "10-49" },
  { id: 3, title: "50-249" },
  { id: 4, title: "250-999" },
  { id: 5, title: "1000+" },
];

const MODAL_WIDTHS = {
  FORM: "w-[520px]",
  SUCCESS: "w-[480px]",
} as const;

const DEFAULT_COMPANY_SIZE = COMPANY_SIZE_OPTIONS[0].title;

const ContactUsForm: React.FC<ContactUsFormProps> = ({ show, onClose, type, backdropDisabled = false }) => {
  const { t } = useTranslation();
  const validationSchema = useValidationSchema((vt) =>
    yup
      .object({
        firstName: yup.string().required(vt("validation:support.field_required")),
        lastName: yup.string().required(vt("validation:support.field_required")),
        businessEmail: yup.string().required(vt("validation:auth.email_required")).email(vt("validation:auth.email_required")),
        companyName: yup.string().required(vt("validation:support.field_required")),
        companySize: yup.string().required(vt("validation:support.field_required")),
        message: yup.string().required(vt("validation:support.field_required")),
      })
      .required()
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const defaultValues = useMemo<ContactFormData>(
    () => ({
      firstName: "",
      lastName: "",
      businessEmail: "",
      companyName: "",
      companySize: DEFAULT_COMPANY_SIZE,
      message: "",
    }),
    []
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ContactFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const onValid = useCallback(
    async (data: ContactFormData) => {
      setErrorMessage(null);
      try {
        await utilsService.contactUs({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.businessEmail,
          company: data.companyName,
          company_size: data.companySize,
          message: data.message,
          type,
        });
        setIsSubmitted(true);
        reset(); // Reset form after successful submission
      } catch (error) {
        console.error("Failed to submit contact form", { status: httpStatusOf(error) });
        setErrorMessage(error instanceof Error ? error.message : t("common:modals.contact_us.error_submit"));
      } finally {
        setSendDisabled(false);
      }
    },
    [type, reset, t]
  );

  const onHandleSubmit = useCallback(() => {
    setErrorMessage(null);
    setSendDisabled(true);
    handleSubmit(onValid, () => {
      // REF-6708: never the FieldErrors themselves — each entry holds the input's ref, and its value.
      console.error("Contact form is invalid");
      setSendDisabled(false);
    })();
  }, [handleSubmit, onValid]);

  const handleCompanySizeChange = useCallback(
    (option: { id: number; title: string }) => {
      setValue("companySize", option.title);
    },
    [setValue]
  );

  const modalClassName = useMemo(() => clsx("!border-trueBlack overflow-hidden overflow-y-scroll no-scrollbar", !isSubmitted ? MODAL_WIDTHS.FORM : MODAL_WIDTHS.SUCCESS), [isSubmitted]);

  // Reset form and state when modal closes
  useEffect(() => {
    if (!show) {
      setIsSubmitted(false);
      setErrorMessage(null);
      setSendDisabled(false);
      reset();
    }
  }, [show, reset]);

  if (!isSubmitted) {
    return (
      <Modal
        onClose={onClose}
        show={show}
        bodyClassName={modalClassName}
        ariaLabel={t("common:modals.contact_us.aria_form")}
        ariaDescribedBy="contact-form-description"
        backdropDisabled={backdropDisabled}
      >
        <div className="flex flex-col gap-10 p-5 bg-trueWhite max-w-[520px] w-full" id="contact-form-description">
          <h2 className="text-h2 w-full text-center ">{t("common:modals.contact_us.form_title")}</h2>
          <div className="flex flex-col gap-3 w-full">
            <div className="flex gap-3 w-full">
              <div className="flex flex-col gap-2.5 w-full">
                <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.first_name")}</span>
                <Input {...register("firstName")} containerClassName="!bg-trueWhite" className="text-trueBlack auth-input" error={errors.firstName?.message} />
              </div>
              <div className="flex flex-col gap-2.5 w-full">
                <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.last_name")}</span>
                <Input {...register("lastName")} containerClassName="!bg-trueWhite" className="text-trueBlack auth-input" error={errors.lastName?.message} />
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.business_email")}</span>
              <Input {...register("businessEmail")} containerClassName="!bg-trueWhite" className="text-trueBlack auth-input" error={errors.businessEmail?.message} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.company_name")}</span>
              <Input {...register("companyName")} containerClassName="!bg-trueWhite" className="text-trueBlack auth-input" error={errors.companyName?.message} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.company_size")}</span>
              <Select isLightMode fixedLightMode defaultId={1} className="w-full !text-trueBlack" options={COMPANY_SIZE_OPTIONS} onChange={handleCompanySizeChange} />
            </div>
            <div className="flex flex-col gap-2.5">
              <span className="text-h7 text-trueBlack">{t("common:modals.contact_us.fields.message")}</span>
              <Textarea {...register("message")} containerClassName="!bg-trueWhite" error={errors.message?.message} className="h-[100px] !resize-none !text-trueBlack auth-input" />
            </div>
            {errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-sm text-red-600">{errorMessage}</span>
              </div>
            )}
            <div className="flex gap-2.5">
              <Button type="button" className="btn-secondary !text-trueBlack hover:!text-trueWhite hover:!bg-trueBlack w-full" onClick={onClose}>
                {t("common:modals.contact_us.buttons.cancel")}
              </Button>
              <Button type="button" disabled={sendDisabled} className="btn-primary w-full" onClick={onHandleSubmit}>
                {sendDisabled ? t("common:modals.contact_us.buttons.sending") : t("common:modals.contact_us.buttons.send")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} show={show} bodyClassName={modalClassName} ariaLabel={t("common:modals.contact_us.aria_success")} backdropDisabled={backdropDisabled}>
      <div className="flex flex-col items-center gap-10 bg-trueWhite max-w-[480px] w-full p-5">
        <img src={ImageSuccessLogo} alt={t("common:modals.contact_us.aria_image_success")} className="w-[75px] scale-x-[-1]" />
        <h2 className="text-h2 text-center">{t("common:modals.contact_us.success_title")}</h2>
        <span className="text-bodyMd text-center">{t("common:modals.contact_us.success_body")}</span>
        <Button type="button" className="btn-primary w-full" onClick={onClose}>
          {t("common:modals.contact_us.buttons.close")}
        </Button>
      </div>
    </Modal>
  );
};

export default React.memo(ContactUsForm);
